import React, { ReactElement, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { generateCouponCode } from "helpers/configurator";

import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import AppModal from "components/shared/modal/AppModal";

import { postEditCoupon } from "redux/handlers/couponHandler";

import { useAppDispatch } from "hooks/reduxHook";

import { ICouponData } from "types/api/couponApiInterface";

type couponData = {
 code: string;
 percentage: number;
 redeem_max: number;
 times_redeemed: number;
};

const EditCoupon = ({
 coupon,
 reloadData,
 closeMenu
}: {
 coupon: ICouponData;
 reloadData?: () => void;
 closeMenu: () => void;
}): ReactElement => {
 const intl = useIntl();
 const dispatch = useAppDispatch();

 const [openModal, setOpenModal] = useState<boolean>(false);
 const [isLoading, setIsLoading] = useState<boolean>(false);

 const handleOpen = () => {
  reset();
  setOpenModal(true);
 };
 const handleClose = () => setOpenModal(false);

 const { control, handleSubmit, watch, setValue, reset } = useForm({
  defaultValues: {
   code: coupon.code,
   percentage: coupon.percentage,
   redeem_max: coupon.redeem_max,
   times_redeemed: coupon.times_redeemed
  }
 });

 const onSubmit: SubmitHandler<couponData> = async (data: couponData) => {
  setIsLoading(true);
  await dispatch(
   postEditCoupon(
    coupon.id,
    data.code,
    Number(data.percentage),
    Number(data.redeem_max),
    Number(data.times_redeemed)
   )
  );
  setIsLoading(false);
  reloadData && reloadData();
  setOpenModal(false);
  closeMenu();
 };

 const checkDisabled = (): boolean => {
  return (
   watch("code") === "" ||
   watch("percentage") <= 0 ||
   watch("redeem_max") < -1 ||
   watch("redeem_max") === 0 ||
   watch("times_redeemed") < 0
  );
 };

 return (
  <>
   <MenuItem onClick={handleOpen}>
    <FormattedMessage id="configurator.edit.coupon" />
   </MenuItem>
   <AppModal
    open={openModal}
    close={handleClose}
    handleClose={handleClose}
    loading={isLoading}
    disabled={isLoading || checkDisabled()}
    handleConfirm={handleSubmit(onSubmit)}
    title={intl.formatMessage({ id: "configurator.edit.coupon" })}>
    <form onSubmit={handleSubmit(onSubmit)}>
     <Stack spacing={2}>
      <Typography variant="subtitle2">
       <FormattedMessage id="configurator.add.couponExplain" />
      </Typography>
      <Stack direction="row" alignItems="center" spacing={2}>
       <Controller
        name={"code"}
        control={control}
        rules={{
         required: true
        }}
        render={({ field }) => (
         <TextField
          {...field}
          fullWidth={true}
          size="small"
          onChange={({ currentTarget }) => setValue("code", currentTarget.value.toUpperCase())}
          label={intl.formatMessage({ id: "configurator.coupon.code" })}
          InputLabelProps={{ shrink: true }}
          sx={{ my: 2 }}
          autoComplete="off"
         />
        )}
       />
       <Button onClick={() => setValue("code", generateCouponCode())} sx={{ minWidth: 200 }}>
        <FormattedMessage id="configurator.coupon.generateCode" />
       </Button>
      </Stack>
      <Controller
       name={"percentage"}
       control={control}
       rules={{
        required: true
       }}
       render={({ field }) => (
        <TextField
         {...field}
         fullWidth={true}
         size="small"
         type="number"
         label={intl.formatMessage({ id: "configurator.coupon.percentage" })}
         InputLabelProps={{ shrink: true }}
         autoComplete="off"
        />
       )}
      />
      <Controller
       name={"redeem_max"}
       control={control}
       rules={{
        required: true
       }}
       render={({ field }) => (
        <TextField
         {...field}
         fullWidth={true}
         size="small"
         type="number"
         label={intl.formatMessage({ id: "configurator.coupon.redeemMax" })}
         InputLabelProps={{ shrink: true }}
         autoComplete="off"
        />
       )}
      />
      <Controller
       name={"times_redeemed"}
       control={control}
       rules={{
        required: true
       }}
       render={({ field }) => (
        <TextField
         {...field}
         fullWidth={true}
         size="small"
         type="number"
         label={intl.formatMessage({ id: "configurator.coupon.redeemed" })}
         InputLabelProps={{ shrink: true }}
         autoComplete="off"
        />
       )}
      />
     </Stack>
    </form>
   </AppModal>
  </>
 );
};

export default EditCoupon;
